import { useEffect } from 'preact/hooks';
import { useLocation } from 'wouter-preact';
import { Compass, Construction } from 'lucide-preact';
import { Section } from '@/components/surfaces';
import { EmptyState } from '@/components/feedback/EmptyState';
import { Button } from '@/components/primitives/Button';
import { setCurrentPath } from '@/stores/navigation';

export interface PlaceholderPageProps {
  title: string;
  kicker?: string;
  /**
   * Variant of the placeholder:
   * - `'wip'` (default): "Página en construcción" — for routes intentionally
   *   reservadas en el mapa de producto pero sin implementación todavía.
   * - `'not-found'`: real 404 — la URL no existe.  Copy diferente para
   *   no confundir con un "todavía no está listo" cuando en realidad la
   *   URL es errónea.
   */
  variant?: 'wip' | 'not-found';
}

export function PlaceholderPage({ title, kicker, variant = 'wip' }: PlaceholderPageProps) {
  const [path, navigate] = useLocation();
  useEffect(() => {
    setCurrentPath(path);
  }, [path]);

  const isNotFound = variant === 'not-found';
  return (
    <div className="px-6 py-6">
      <div className="mx-auto max-w-4xl">
        <Section kicker={kicker} title={title}>
          <EmptyState
            icon={isNotFound ? Compass : Construction}
            title={isNotFound ? 'Esta página no existe' : 'Página en construcción'}
            description={
              isNotFound
                ? 'No encontramos la URL que pediste. Puede haber cambiado, estar mal escrita, o nunca haber existido.'
                : 'Esta sección todavía no está lista para operación diaria. La navegación queda reservada para mantener estable el mapa del producto.'
            }
            action={
              <Button variant="primary" onClick={() => navigate('/')}>
                Volver al inicio
              </Button>
            }
          />
        </Section>
      </div>
    </div>
  );
}
